import { useLiveProfile, type DepthRung } from "../hooks/useLiveProfile.ts";
import { fmtNotional } from "../lib/depth.ts";

// Shared palette (matches SpreadCaptureCard / QuoteQualityCard).
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// Bands mirror DexDepthCard: touch-anchored bps rungs read straight off the
// live-profile ladder (the SAME reconstructed book DepthLadder renders), so the
// card and the ladder are one book at one anchor and cannot disagree. Labelled
// in bps (not ±0.5/1/2%) to be honest about touch-anchoring — the percentage-
// from-mid figures live in the precompute-backed RFP/compliance views.
const CARD_BANDS: { bps: number; label: string }[] = [
  { bps: 50, label: "±50bps" },
  { bps: 100, label: "±100bps" },
  { bps: 200, label: "±200bps" },
];

function rungAt(rungs: DepthRung[] | undefined, bps: number): DepthRung | undefined {
  return rungs?.find((r) => r.bps === bps);
}

// Map the live-profile status to a badge treatment (mirrors the old
// statusMeta shape: ok / label / color / reason).
function profileStatusMeta(status: string | undefined): {
  ok: boolean;
  label: string;
  color: string;
  reason: string;
} {
  switch (status) {
    case "ok":
      return { ok: true, label: "ok", color: "#5fb87a", reason: "two-sided book reconstructed" };
    case "one_sided":
      return {
        ok: false,
        label: "one-sided",
        color: AMBER,
        reason: "only one side of the book is present at the anchor",
      };
    case "no_anchor":
      return {
        ok: false,
        label: "no book",
        color: MUTED,
        reason: "no recent book_l2 snapshot to reconstruct from",
      };
    default:
      return { ok: false, label: status ?? "—", color: MUTED, reason: "" };
  }
}

export function DepthCard({
  venue,
  pair,
  clientId: _clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  // clientId is context-only; live-profile is market-grain and does not filter.
  const q = useLiveProfile(venue, pair);
  const prof = q.data;
  const meta = profileStatusMeta(prof?.status);
  const quoteAsset = pair.split("/")[1] ?? "";

  // Headline = ±200bps cumulative quote-notional, bid + ask (the contractable
  // depth figure — same definition as before, now from the live book that the
  // ladder shows). null if that rung is not measurable on either side.
  const bid200 = rungAt(prof?.bid_rungs, 200)?.cumulative_quote ?? null;
  const ask200 = rungAt(prof?.ask_rungs, 200)?.cumulative_quote ?? null;
  const headline =
    bid200 != null && ask200 != null ? bid200 + ask200 : null;

  // Greyed when status isn't ok: values stay visible (reference) but muted.
  const valueColor = meta.ok ? TEXT : MUTED;

  return (
    <div
      style={{
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: "12px 14px",
        minWidth: 240,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, color: MUTED }}>Book depth</span>
        <span
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 5,
            color: meta.color,
            border: `1px solid ${meta.color}55`,
          }}
          title={meta.reason}
        >
          {meta.label}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span
          style={{
            fontSize: 24,
            fontWeight: 500,
            color: valueColor,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtNotional(headline)}
        </span>
        <span style={{ fontSize: 12, color: MUTED }}>
          {prof?.notional_unit ?? quoteAsset} ±200bps (bid+ask)
        </span>
      </div>

      {/* per-band cumulative quote-notional, bid vs ask — read off ladder rungs */}
      <div style={{ marginTop: 10 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr 1fr",
            gap: "3px 12px",
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            color: valueColor,
          }}
        >
          <span style={{ color: MUTED, fontSize: 11 }} />
          <span style={{ color: MUTED, fontSize: 11 }}>bid</span>
          <span style={{ color: MUTED, fontSize: 11 }}>ask</span>
          {CARD_BANDS.map(({ bps, label }) => {
            const b = rungAt(prof?.bid_rungs, bps);
            const a = rungAt(prof?.ask_rungs, bps);
            return (
              <Band
                key={bps}
                label={label}
                bidQ={b?.cumulative_quote ?? null}
                askQ={a?.cumulative_quote ?? null}
                bidMeasurable={b?.measurable ?? false}
                askMeasurable={a?.measurable ?? false}
              />
            );
          })}
        </div>
      </div>

      {/* anchor footnote — the SAME anchor the ladder shows, so a client can see
          both panels are one book at one instant. */}
      <div
        style={{
          fontSize: 11,
          color: MUTED,
          marginTop: 8,
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 7,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {prof && prof.anchor_ts ? (
          <>
            live book · anchor{" "}
            {prof.anchor_age_seconds == null ? "—" : `${prof.anchor_age_seconds}s`}
          </>
        ) : (
          "no book"
        )}
      </div>

      {!meta.ok && prof && prof.status !== "no_anchor" && meta.reason && (
        <div style={{ fontSize: 11, color: meta.color, marginTop: 6, lineHeight: 1.4 }}>
          {meta.reason}
        </div>
      )}
      {q.isError && (
        <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>could not load</div>
      )}
    </div>
  );
}

function Band({
  label,
  bidQ,
  askQ,
  bidMeasurable,
  askMeasurable,
}: {
  label: string;
  bidQ: number | null;
  askQ: number | null;
  bidMeasurable: boolean;
  askMeasurable: boolean;
}) {
  return (
    <>
      <span style={{ color: MUTED, fontSize: 11 }}>{label}</span>
      <span title={bidMeasurable ? undefined : "band not measurable at this anchor"}>
        {fmtNotional(bidQ)}
      </span>
      <span title={askMeasurable ? undefined : "band not measurable at this anchor"}>
        {fmtNotional(askQ)}
      </span>
    </>
  );
}
